import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { BookOpen, Loader2, ArrowLeft, Mail, CheckCircle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  const sendResetEmail = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/request-password-reset`, { email });
      setSubmitted(true);
      setResendCount(prev => prev + 1);
      toast.success('Reset link sent! Check your email inbox.');
      
      // Start cooldown timer for resend
      setResendCooldown(60);
      const timer = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Password reset request failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await sendResetEmail();
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    await sendResetEmail();
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="forgot-password-success">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 p-4 rounded-full">
                <Mail className="h-10 w-10 text-green-600" />
              </div>
            </div>
            <CardTitle className="font-serif text-2xl">Check Your Email!</CardTitle>
            <CardDescription className="text-base mt-2">
              We sent a password reset link to:
            </CardDescription>
            <p className="font-medium text-primary mt-1">{email}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Simple Instructions */}
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <p className="text-sm font-medium">What to do next:</p>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Open your email inbox</li>
                <li>Look for an email from <strong>Brookzone Bookseller</strong></li>
                <li>Click the "Reset My Password" button in the email</li>
              </ol>
            </div>

            {/* Can't find email */}
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800 mb-2">Can't find the email?</p>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Check your <strong>Spam</strong> or <strong>Junk</strong> folder</li>
                <li>• Make sure <strong>{email}</strong> is correct</li>
                <li>• Wait 1-2 minutes for the email to arrive</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Resend Button */}
              <Button 
                variant="outline" 
                onClick={handleResend}
                disabled={loading || resendCooldown > 0}
                className="w-full"
                data-testid="resend-email-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </span>
                ) : resendCooldown > 0 ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Resend in {resendCooldown}s
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Resend Email
                  </span>
                )}
              </Button>

              {/* Try Different Email */}
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }}
                className="w-full"
                data-testid="try-another-email-btn"
              >
                Use a different email
              </Button>

              {/* Back to Login */}
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full text-muted-foreground" data-testid="back-to-login-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>

            {resendCount > 1 && (
              <p className="text-xs text-center text-muted-foreground">
                Email sent {resendCount} times. Still not receiving? Contact support.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <BookOpen className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="font-serif text-2xl">Forgot Your Password?</CardTitle>
          <CardDescription>
            No worries! Enter your email and we'll send you a reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="pl-10"
                  autoFocus
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full btn-accent" 
              disabled={loading || !email}
              data-testid="forgot-password-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending Reset Link...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
